import { useState } from 'react';
import { ChevronLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { createConsentAndCheckout } from '@/features/subscription/api';
import { useSubscriptionPlans } from '@/hooks/useSubscriptionPlans';
import type { PublicPlan } from '@/features/subscription/plans-api';
import { phCapture } from '@/lib/analytics/posthog';
import { ApiError, NetworkError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';

/**
 * D-09 Bestellübersicht — `/checkout/review?plan={planSlug}`.
 *
 * The user lands here from A-02 after clicking a Tier-Card or PackCard
 * CTA. Renders an order-summary table with briefing-verbatim copy,
 * gates the primary CTA on the §356 BGB withdrawal-waiver checkbox,
 * and on confirm POSTs `/api/subscriptions/create-consent-and-checkout`
 * before redirecting to Stripe-hosted checkout via `window.location.href`.
 *
 * Plan resolution:
 *   - `planSlug` from `?plan=...`. Missing or no-match → render the
 *     `plan-not-found` error block with a "← Zurück zur Übersicht" CTA.
 *   - Plans fetched via `useSubscriptionPlans()` — cache hit if user
 *     came from A-02; cache miss is fine (skeleton state until the
 *     first fetch resolves).
 *
 * Variant rendering:
 *   - mode='subscription' + 'monthly' → priceMonthly, durationSubscriptionMonthly,
 *     "Geschichten pro Monat:" row
 *   - mode='subscription' + 'yearly'  → priceYearly (total + per-month),
 *     durationSubscriptionYearly, "Geschichten pro Monat:" row
 *   - mode='payment'                  → pricePack, durationPack,
 *     "Geschichten:" row (NOT "pro Monat")
 *
 * Briefing-LOCKED strings (Verbatim-Wordings appendix):
 *   - `consentCheckbox`         (legal-reviewed §356 BGB waiver)
 *   - `footerText`              (Stripe-handoff disclosure + AGB / Datenschutz)
 *   - `includedServicesValue`   (D5 — full worlds list, no truncation)
 *
 * Click-handler dispatch on the CTA:
 *   1. Disable button + swap label to `primaryCtaLoading`
 *   2. POST `createConsentAndCheckout(planKey, true)`
 *   3. On success → `window.location.href = checkoutUrl` (leaves SPA)
 *   4. On NetworkError → inline `errors.networkError` banner + re-enable
 *   5. On ApiError    → inline `errors.checkoutFailed` banner + re-enable
 */
export function CheckoutReviewRoute() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const planSlug = searchParams.get('plan');
  const { plans, isLoading: plansLoading } = useSubscriptionPlans();

  const [consentChecked, setConsentChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  // Bug #4 (2026-05-13): added 'alreadyActive' for the duplicate-
  // subscription guard (backend error_code='subscription_already_active',
  // HTTP 409). Renders a styled banner + CTA to /account/subscription
  // instead of the generic checkoutFailed text.
  const [submitError, setSubmitError] = useState<'network' | 'checkout' | 'alreadyActive' | null>(
    null,
  );

  const S = STRINGS.checkout.review;

  const plan = planSlug ? (plans?.find((p) => p.planKey === planSlug) ?? null) : null;

  const handleSubmit = async () => {
    if (!plan || !consentChecked || submitting) return;
    setSubmitting(true);
    setSubmitError(null);
    phCapture('checkout_started', { plan: plan.planKey });
    try {
      const { checkoutUrl } = await createConsentAndCheckout(plan.planKey, true);
      window.location.href = checkoutUrl;
    } catch (err) {
      if (err instanceof NetworkError) {
        setSubmitError('network');
      } else if (err instanceof ApiError && err.errorCode === 'subscription_already_active') {
        // Bug #4 (2026-05-13): duplicate-active-subscription guard.
        setSubmitError('alreadyActive');
      } else if (err instanceof ApiError) {
        setSubmitError('checkout');
      } else {
        setSubmitError('checkout');
      }
      setSubmitting(false);
    }
  };

  // Plan-not-found state (planSlug missing OR planSlug doesn't resolve
  // to a catalog entry once plans are loaded). Distinguishes from the
  // pre-fetch loading state below.
  const planNotFound = !plansLoading && plans !== null && !plan;

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer>
        <div className="flex flex-col gap-5 py-5 md:gap-7 md:pt-10 md:pb-14 desktop:mx-auto desktop:max-w-[768px]">
          <button
            type="button"
            onClick={() => navigate('/account/subscription')}
            className="flex items-center gap-1 self-start text-sm font-medium text-dream-navy hover:text-dream-navy/70"
            data-testid="checkout-review-back"
          >
            <ChevronLeft className="h-4 w-4" strokeWidth={2} />
            <span>{S.back}</span>
          </button>

          <h1 className="text-base font-semibold leading-4 text-dream-navy">{S.title}</h1>

          {planNotFound ? (
            <div
              role="alert"
              data-testid="checkout-review-plan-not-found"
              className="flex flex-col items-start gap-3 rounded-lg border border-error/30 bg-error/10 p-4"
            >
              <p className="text-sm text-error-text">{S.errors.planNotFound}</p>
              <button
                type="button"
                onClick={() => navigate('/account/subscription')}
                className="text-xs font-medium text-dream-navy underline"
                data-testid="checkout-review-plan-not-found-cta"
              >
                {S.errors.planNotFoundCta}
              </button>
            </div>
          ) : (
            <>
              <p className="text-xs leading-5 text-dream-navy/70">{S.subheading}</p>

              <SummaryCard plan={plan} loading={plansLoading} />

              <label
                className="flex items-start gap-2.5 select-none cursor-pointer"
                data-testid="checkout-review-consent-label"
              >
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer accent-dream-gold"
                  data-testid="checkout-review-consent-checkbox"
                  disabled={submitting}
                />
                <span className="text-xs leading-relaxed text-dream-navy">{S.consentCheckbox}</span>
              </label>

              {submitError === 'alreadyActive' ? (
                <div
                  role="alert"
                  data-testid="checkout-review-already-active"
                  className="flex flex-col items-start gap-3 rounded-lg border border-dream-gold/40 bg-dream-gold/12 p-4"
                >
                  <p className="text-sm text-warning-text">{S.errors.alreadyActive}</p>
                  <button
                    type="button"
                    onClick={() => navigate('/account/subscription')}
                    className="text-xs font-medium text-dream-navy underline"
                    data-testid="checkout-review-already-active-cta"
                  >
                    {S.errors.alreadyActiveCta}
                  </button>
                </div>
              ) : (
                submitError && (
                  <p
                    role="alert"
                    className="text-xs text-error"
                    data-testid="checkout-review-submit-error"
                  >
                    {submitError === 'network' ? S.errors.networkError : S.errors.checkoutFailed}
                  </p>
                )
              )}

              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={!plan || !consentChecked || submitting}
                className="flex w-full items-center justify-center overflow-hidden rounded-lg bg-dream-gold px-4 py-[14px] text-xs font-semibold leading-3 text-dream-navy transition-colors hover:bg-dream-gold/90 disabled:opacity-50"
                data-testid="checkout-review-cta"
              >
                {submitting ? S.primaryCtaLoading : S.primaryCta}
              </button>

              <p className="text-xxs leading-4 text-dream-navy/60">{S.footerText}</p>
            </>
          )}
        </div>
      </ContentContainer>
    </div>
  );
}

// ── Route-private SummaryCard ──

interface SummaryCardProps {
  plan: PublicPlan | null;
  loading: boolean;
}

function SummaryCard({ plan, loading }: SummaryCardProps) {
  const S = STRINGS.checkout.review;

  if (loading || !plan) {
    return (
      <div
        className="h-40 w-full animate-pulse rounded-lg bg-dream-navy/5"
        data-testid="checkout-review-summary-loading"
        aria-busy="true"
      />
    );
  }

  const isPack = plan.mode === 'payment';
  const isYearly = plan.billingInterval === 'yearly';

  const priceLine = isPack
    ? S.fieldValues.pricePack.replace('{amount}', formatEuro(plan.amountEuros))
    : isYearly
      ? S.fieldValues.priceYearly
          .replace('{totalAmount}', formatEuro(plan.amountEuros))
          .replace('{perMonthAmount}', formatEuro(plan.amountEuros / 12))
      : S.fieldValues.priceMonthly.replace('{amount}', formatEuro(plan.amountEuros));

  const durationLine = isPack
    ? S.fieldValues.durationPack
    : isYearly
      ? S.fieldValues.durationSubscriptionYearly
      : S.fieldValues.durationSubscriptionMonthly;

  const storiesLabel = isPack ? S.fieldLabels.storiesPack : S.fieldLabels.storiesPerMonth;
  const storiesValue = isPack
    ? S.fieldValues.storiesPackValue.replace('{count}', String(plan.storiesGranted ?? 0))
    : S.fieldValues.storiesPerMonthValue.replace('{count}', String(plan.storiesPerMonth ?? 0));

  const totalLine = S.fieldValues.totalAmountValue.replace(
    '{amount}',
    formatEuro(plan.amountEuros),
  );

  return (
    <div
      className="flex w-full flex-col gap-3 rounded-lg border border-dream-navy/10 bg-dream-white p-4"
      data-testid="checkout-review-summary"
    >
      <SummaryRow label={S.fieldLabels.tariff} value={plan.planName} />
      <SummaryRow label={S.fieldLabels.price} value={priceLine} testId="summary-price" />
      <SummaryRow label={S.fieldLabels.duration} value={durationLine} testId="summary-duration" />
      <SummaryRow label={storiesLabel} value={storiesValue} testId="summary-stories" />
      <SummaryRow
        label={S.fieldLabels.includedServices}
        value={S.includedServicesValue}
        testId="summary-included-services"
      />
      <hr className="border-dream-navy/10" />
      <SummaryRow label={S.fieldLabels.totalAmount} value={totalLine} bold testId="summary-total" />
    </div>
  );
}

interface SummaryRowProps {
  label: string;
  value: string;
  bold?: boolean;
  testId?: string;
}

function SummaryRow({ label, value, bold, testId }: SummaryRowProps) {
  return (
    <div className="flex w-full items-start justify-between gap-3 text-xs leading-relaxed">
      <span className="shrink-0 text-dream-navy/60">{label}</span>
      <span
        className={
          bold ? 'text-right font-semibold text-dream-navy' : 'text-right text-dream-navy/90'
        }
        data-testid={testId}
      >
        {value}
      </span>
    </div>
  );
}

function formatEuro(value: number): string {
  return value.toFixed(2).replace('.', ',');
}
